<template>
  <div>
    <v-dialog v-model="show_dialog_box" max-width="1000px">
      <template v-slot:activator="{ on }">
        <v-icon v-on="on" class="gray1--text">mdi-eye</v-icon>
      </template>
      <v-card class="pa-3">
        <v-card-title>
          <span class="text-h6 dark--text">Change History</span>
          <v-spacer></v-spacer>
          <v-btn color="primary" class="light--text" text @click="close"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-container class="pt-4 pb-2">
          <v-row dense>
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text text-uppercase">Performed By</div>
              <div class="text-body-1 font-weight-medium">{{ performedByName }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text text-uppercase">Date &amp; Time</div>
              <div class="text-body-1 font-weight-medium">{{ formattedDate }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text text-uppercase">Audit Category</div>
              <v-chip small outlined color="primary">{{ categoryLabel }}</v-chip>
            </v-col>
          </v-row>
        </v-container>
        <v-divider></v-divider>
        <v-card-text class="audit-scroll-area">
          <v-simple-table v-if="changes.length" dense class="audit-table mt-3">
            <thead>
              <tr class="primary">
                <th class="white--text">Field</th>
                <th class="white--text">Previous Value</th>
                <th class="white--text">New Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(change, idx) in changes" :key="idx">
                <td class="font-weight-medium">{{ change.field }}</td>
                <td>
                  <span v-if="change.type === 'media'" class="value-pill value-pill--old">(previous value)</span>
                  <img v-else-if="change.type === 'image' && change.from" :src="resolveImageUrl(change.from)" class="value-image" alt="Previous image" />
                  <span v-else-if="change.type === 'image'" class="value-muted">No image</span>
                  <span v-else-if="change.type === 'list'" class="value-wrap">
                    <span v-if="!(change.removed || []).length" class="value-muted">No items removed</span>
                    <span v-for="(v, i) in change.removed" :key="i" class="value-pill value-pill--old">{{ v }}</span>
                  </span>
                  <span v-else class="value-pill value-pill--old">{{ change.from }}</span>
                </td>
                <td>
                  <span v-if="change.type === 'media'" class="value-pill value-pill--new">(updated value)</span>
                  <img v-else-if="change.type === 'image' && change.to" :src="resolveImageUrl(change.to)" class="value-image" alt="Updated image" />
                  <span v-else-if="change.type === 'image'" class="value-muted">No image</span>
                  <span v-else-if="change.type === 'list'" class="value-wrap">
                    <span v-if="!(change.added || []).length" class="value-muted">No items added</span>
                    <span v-for="(v, i) in change.added" :key="i" class="value-pill value-pill--new">{{ v }}</span>
                  </span>
                  <span v-else class="value-pill value-pill--new">{{ change.to }}</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-simple-table v-else-if="details.length" dense class="audit-table mt-3">
            <thead>
              <tr class="primary">
                <th class="white--text">Field</th>
                <th class="white--text">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in details" :key="idx">
                <td class="font-weight-medium">{{ item.field }}</td>
                <td>{{ item.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div v-else-if="submitted.length">
            <p class="grey--text text-caption mb-2">
              This entry predates change tracking - only the values submitted in this edit are known, not what they changed from.
            </p>
            <v-simple-table dense class="audit-table">
              <thead>
                <tr class="primary">
                  <th class="white--text">Field</th>
                  <th class="white--text">Submitted Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in submitted" :key="idx">
                  <td class="font-weight-medium">{{ item.field }}</td>
                  <td>{{ item.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <v-simple-table v-else-if="deletedItems.length" dense class="audit-table mt-3">
            <thead>
              <tr class="primary">
                <th class="white--text">Field</th>
                <th class="white--text">Value (at time of deletion)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in deletedItems" :key="idx">
                <td class="font-weight-medium">{{ item.field }}</td>
                <td>{{ item.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-container v-else class="mt-2">
            <p class="grey--text text-caption mb-2">No structured details are available for this entry.</p>
            <pre class="audit-json">{{ rawFallback }}</pre>
          </v-container>

          <div v-if="hasLegacyDetail" class="mt-4">
            <v-btn text small color="primary" class="pl-0" @click="showLegacy = !showLegacy">
              <v-icon small class="me-1">{{ showLegacy ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              {{ showLegacy ? "Hide" : "View" }} original raw data
            </v-btn>
            <pre v-if="showLegacy" class="audit-json mt-2">{{ legacyFormatted }}</pre>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import moment from "moment";
import "moment-timezone";
import { TIME_ZONE } from "../../../utils/utils";

export default {
  props: {
    auditLog: {
      default: true,
    },
  },
  data() {
    return {
      show_dialog_box: false,
      showLegacy: false,
    };
  },
  computed: {
    hasLegacyDetail() {
      return !!this.auditLog.legacyAdditionalDetail;
    },
    legacyFormatted() {
      try {
        return JSON.stringify(JSON.parse(this.auditLog.legacyAdditionalDetail), null, 2);
      } catch (error) {
        return this.auditLog.legacyAdditionalDetail;
      }
    },
    parsedDetail() {
      try {
        const parsed = JSON.parse(this.auditLog.additionalDetail);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (error) {
        return {};
      }
    },
    changes() {
      return Array.isArray(this.parsedDetail.changes) ? this.parsedDetail.changes : [];
    },
    details() {
      return Array.isArray(this.parsedDetail.details) ? this.parsedDetail.details : [];
    },
    submitted() {
      return Array.isArray(this.parsedDetail.submitted) ? this.parsedDetail.submitted : [];
    },
    deletedItems() {
      return Array.isArray(this.parsedDetail.deleted) ? this.parsedDetail.deleted : [];
    },
    rawFallback() {
      try {
        return JSON.stringify(JSON.parse(this.auditLog.additionalDetail), null, 2);
      } catch (error) {
        return this.auditLog.additionalDetail;
      }
    },
    performedByName() {
      const user = this.auditLog.updatedBy;
      if (!user) return "—";
      const first = user.firstnameEn ? user.firstnameEn[0].toUpperCase() + user.firstnameEn.substring(1) : "";
      const last = user.lastnameEn ? user.lastnameEn[0].toUpperCase() + user.lastnameEn.substring(1) : "";
      return `${first} ${last}`.trim() || "—";
    },
    formattedDate() {
      if (!this.auditLog.updatedTime) return "—";
      const timestamp = parseInt(this.auditLog.updatedTime);
      const date = isNaN(timestamp) ? this.auditLog.updatedTime : new Date(timestamp);
      return moment(date).tz(TIME_ZONE).format("MMM DD YYYY hh:mm A");
    },
    categoryLabel() {
      const type = this.auditLog.auditType;
      if (!type) return "—";
      return type
        .replace(/_LOG$/, "")
        .split("_")
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(" ");
    },
  },
  methods: {
    close() {
      this.show_dialog_box = false;
    },
    resolveImageUrl(path) {
      if (!path) return "";
      if (/^https?:\/\//i.test(path)) return path;
      return `${process.env.VUE_APP_BASE_URL}${path}`;
    },
  },
};
</script>
<style scoped>
.audit-scroll-area {
  max-height: 55vh;
  overflow-y: auto;
}
.audit-table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  overflow: hidden;
}
.audit-json {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 60vh;
  overflow-y: auto;
}
.value-wrap {
  display: flex;
  flex-wrap: wrap;
}
.value-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.6;
  margin: 2px 4px 2px 0;
  white-space: nowrap;
}
.value-pill--old {
  background-color: #fde2e2;
  color: #c62828;
}
.value-pill--new {
  background-color: #dcf5e3;
  color: #2e7d32;
}
.value-muted {
  font-size: 12px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.4);
}
.value-image {
  max-width: 120px;
  max-height: 80px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: block;
}
</style>
